import {
  Modal,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
  FlatList,
  Platform,
} from "react-native";
import React, { useState, useEffect, useRef } from "react";
import { AppColors } from "../../../../utility/AppColors";
import { s } from "react-native-size-matters";
import DateTimePicker from "react-native-ui-datepicker";
import Ionicons from "@expo/vector-icons/Ionicons";
import { AppFontStyle } from "../../../../styles/AppFontStyle";
import dayjs from "dayjs";
import "dayjs/locale/en";

if (Platform.OS === "web") {
  const style = document.createElement("style");
  style.textContent = `
    /* SCOPED: Only affect DateTimePicker modal */
    .datetime-picker-modal .css-text-146c3p1,
    .datetime-picker-modal [class*="css-text"],
    .datetime-picker-modal [class*="header"],
    .datetime-picker-modal [class*="month"],
    .datetime-picker-modal [class*="year"],
    .datetime-picker-modal [class*="label"] {
      color: ${AppColors.white} !important;
    }

    /* Target specific calendar text elements inside modal only */
    .datetime-picker-modal div[class*="css-text"] {
      color: ${AppColors.white} !important;
    }

    /* Override any black text in the calendar inside modal only */
    .datetime-picker-modal .react-native-ui-datepicker * {
      color: ${AppColors.white} !important;
    }

    /* Specific class targeting inside modal only */
    .datetime-picker-modal .css-text-146c3p1 {
      color: ${AppColors.white} !important;
    }
  `;
  document.head.appendChild(style);
}

// Add custom parsing plugin for consistent date format handling
import customParseFormat from "dayjs/plugin/customParseFormat";
dayjs.extend(customParseFormat);

const DateTimePickerModal = (props) => {
  const {
    showModal = false,
    setShowModal,
    selectedDate,
    timePicker = false,
    selectDate = dayjs().format("DD/MM/YYYY"), // Default to today in correct format
    selectTime = "00:00",
  } = props;

  const [selectedDay, setSelectedDay] = useState(() => {
    // Parse selectDate with explicit DD/MM/YYYY format
    const parsedDate = dayjs(selectDate, "DD/MM/YYYY", true);
    return parsedDate.isValid() ? parsedDate : dayjs();
  });

  const [selectedTime, setSelectedTime] = useState(() => {
    const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
    return timeRegex.test(selectTime) ? selectTime : "00:00";
  });

  const allTimeOptions = Array.from({ length: 48 }, (_, i) => {
    const hours = Math.floor(i / 2);
    const minutes = i % 2 === 0 ? "00" : "30";
    return `${hours.toString().padStart(2, "0")}:${minutes}`;
  });
  const flatListRef = useRef(null);

  const getNextThirtyMinuteInterval = (date) => {
    const now = dayjs(date);
    const minTime = now.add(2, "hours");
    const hours = minTime.hour();
    const minutes = minTime.minute();

    let nextMinutes = minutes > 0 && minutes <= 30 ? 30 : 0;
    let nextHour = nextMinutes === 0 && minutes > 30 ? hours + 1 : hours;
    if (nextMinutes === 0 && minutes === 0) {
      nextMinutes = 30;
    } else if (nextMinutes === 30 && minutes <= 30) {
      nextHour = hours;
    }

    if (nextHour >= 24) {
      return { date: now.add(1, "day").startOf("day"), time: "00:00" };
    }

    const time = `${nextHour.toString().padStart(2, "0")}:${nextMinutes}`;
    return { date: now.startOf("day"), time };
  };

  const getFilteredTimeOptions = () => {
    const now = dayjs();
    const minTime = now.add(2, "hours");
    const isToday = selectedDay.isSame(now, "day");

    return allTimeOptions.filter((time) => {
      const [hour, minute] = time.split(":").map(Number);
      const timeDate = selectedDay.hour(hour).minute(minute);

      if (isToday) {
        return timeDate.isAfter(minTime) || timeDate.isSame(minTime);
      }
      return true;
    });
  };
  useEffect(() => {
    const filteredOptions = getFilteredTimeOptions();
    if (!filteredOptions.includes(selectedTime) && timePicker) {
      setSelectedTime(filteredOptions[0] || "00:00");
    }
  }, [selectedDay, selectDate, selectTime, timePicker]);

  useEffect(() => {
    if (showModal && timePicker && flatListRef.current && selectedTime) {
      const index = allTimeOptions.indexOf(selectedTime);
      if (index !== -1) {
        flatListRef.current.scrollToIndex({
          index,
          animated: true,
          viewPosition: 0.5,
        });
      }
    }
  }, [showModal, selectedTime, timePicker]);

  const onCloseClick = () => {
    setShowModal(false);
  };

  const renderTimeOption = ({ item }) => {
    const now = dayjs();
    const minTime = now.add(2, "hours");
    const isToday = selectedDay.isSame(now, "day");
    const [hour, minute] = item.split(":").map(Number);
    const timeDate = selectedDay.hour(hour).minute(minute);
    const isPast = isToday && timeDate.isBefore(minTime);
    const filteredOptions = getFilteredTimeOptions();
    const isDisabled = !filteredOptions.includes(item);

    return (
      <TouchableOpacity
        style={[
          styles.timeOption,
          selectedTime === item && {
            backgroundColor: AppColors.appViolentColor_6D08C3,
          },
          isDisabled && { opacity: 0.5 },
        ]}
        onPress={() => !isDisabled && setSelectedTime(item)}
        disabled={isDisabled}
      >
        <Text
          style={[
            AppFontStyle.MEDIUM_ANYBODY_14,
            {
              color: selectedTime === item ? AppColors.white : AppColors.white,
            },
          ]}
        >
          {item}
        </Text>
      </TouchableOpacity>
    );
  };

  return (
    <Modal
      animationType="fade"
      transparent={true}
      visible={showModal}
      onRequestClose={onCloseClick}
    >
      <View
        style={[
          styles.container,
          { justifyContent: "center", alignItems: "center" },
        ]}
        // @ts-ignore - className is used for web only
        className="datetime-picker-modal"
      >
        <View style={styles.datePicker}>
          <View
            style={{
              flexDirection: "row",
              marginTop: 20,
              marginHorizontal: 15,
            }}
          >
            <Text
              style={[
                AppFontStyle.BOLD_ANYBODY_14,
                {
                  color: AppColors.white,
                  flex: 1,
                  alignSelf: "center",
                },
              ]}
            >
              Select Date & Time
            </Text>
            <Ionicons
              name="close"
              size={28}
              color={AppColors.white}
              onPress={onCloseClick}
            />
          </View>

          <View style={styles.datePickerContainer}>
            <View style={styles.datePickerView}>
              <DateTimePicker
                mode="single"
                date={selectedDay.toDate()}
                locale={"en"}
                timePicker={false}
                theme="dark"
                onChange={(params) => {
                  const newDay = dayjs(params.date);
                  setSelectedDay(newDay);
                  const filteredOptions = getFilteredTimeOptions();
                  if (!filteredOptions.includes(selectedTime) && timePicker) {
                    setSelectedTime(filteredOptions[0] || "00:00");
                  }
                }}
                minDate={new Date()}
                //@ts-ignore
                style={{
                  flex: 1,
                  backgroundColor: AppColors.black_121212,
                }}
                styles={{
                  calendarContainer: {
                    backgroundColor: AppColors.black_121212,
                  },
                  monthContainer: {
                    backgroundColor: AppColors.black_121212,
                  },
                  yearContainer: {
                    backgroundColor: AppColors.black_121212,
                  },
                  weekDaysContainer: {
                    backgroundColor: AppColors.black_121212,
                  },
                  disabled_label: {
                    color: AppColors.gray_BBB,
                  },
                  selected: {
                    backgroundColor: AppColors.appViolentColor_6D08C3,
                    color: AppColors.white,
                    borderRadius: 25,
                  },
                  selected_label: {
                    color: AppColors.white,
                  },
                  header: {
                    backgroundColor: AppColors.black_282828,
                    color: AppColors.white,
                    paddingHorizontal: 10,
                    paddingVertical: 5,
                    borderRadius: 5,
                    fontFamily: "Anybody_500Medium",
                    fontSize: 14,
                  },
                  header_label: {
                    color: AppColors.white,
                    fontFamily: "Anybody_500Medium",
                    fontSize: 16,
                  },
                  header_text: {
                    color: AppColors.white,
                    fontFamily: "Anybody_500Medium",
                    fontSize: 16,
                  },
                  header_title: {
                    color: AppColors.white,
                    fontFamily: "Anybody_500Medium",
                    fontSize: 16,
                  },
                  weekday_label: {
                    color: AppColors.white,
                    fontFamily: "Anybody_500Medium",
                    fontSize: 14,
                  },
                  today: {
                    color: AppColors.white,
                    fontFamily: "Anybody_500Medium",
                    fontSize: 14,
                  },
                  month_label: {
                    color: AppColors.white,
                    fontFamily: "Anybody_500Medium",
                    fontSize: 16,
                  },
                  day_label: {
                    color: AppColors.white,
                    fontFamily: "Anybody_500Medium",
                    fontSize: 14,
                  },
                  year_label: {
                    color: AppColors.white,
                    fontFamily: "Anybody_500Medium",
                    fontSize: 14,
                  },
                  month_year_label: {
                    color: AppColors.white,
                    fontFamily: "Anybody_500Medium",
                    fontSize: 16,
                  },
                  arrow_left: {
                    color: AppColors.white,
                  },
                  arrow_right: {
                    color: AppColors.white,
                  },
                  monthYearLabel: {
                    color: AppColors.white,
                    fontFamily: "Anybody_500Medium",
                    fontSize: 16,
                  },
                  headerLabel: {
                    color: AppColors.white,
                    fontFamily: "Anybody_500Medium",
                    fontSize: 16,
                  },
                  text: {
                    color: AppColors.white,
                  },
                  month_selector_label: {
                    color: AppColors.white,
                  },
                  year_selector_label: {
                    color: AppColors.white,
                  },
                }}
              />
              {timePicker && (
                <View style={styles.timePickerContainer}>
                  <FlatList
                    ref={flatListRef}
                    showsHorizontalScrollIndicator={false}
                    data={allTimeOptions}
                    renderItem={renderTimeOption}
                    keyExtractor={(item) => item}
                    horizontal
                    contentContainerStyle={styles.timeListContent}
                    style={styles.timeList}
                    onScrollToIndexFailed={(info) => {
                      const wait = new Promise((resolve) =>
                        setTimeout(resolve, 500)
                      );
                      wait.then(() => {
                        flatListRef.current?.scrollToIndex({
                          index: info.index,
                          animated: true,
                          viewPosition: 0.5,
                        });
                      });
                    }}
                  />
                </View>
              )}
            </View>
          </View>

          <View
            style={{
              flexDirection: "row",
              marginHorizontal: 15,
            }}
          >
            <Text
              style={[
                AppFontStyle.MEDIUM_ANYBODY_12,
                {
                  color: AppColors.white,
                  flex: 1,
                  alignSelf: "center",
                },
              ]}
            >
              {selectedDay && (timePicker ? selectedTime : true)
                ? `${selectedDay.format("DD/MM/YYYY")}${
                    timePicker ? " " + selectedTime : ""
                  }`
                : ""}
            </Text>
            <TouchableOpacity
              style={{
                backgroundColor: AppColors.appViolentColor_6D08C3,
                borderRadius: 10,
                marginBottom: 15,
              }}
              onPress={() => {
                if (selectedDay) {
                  let finalDate = selectedDay;
                  if (timePicker && selectedTime) {
                    const [hours, minutes] = selectedTime.split(":");
                    finalDate = selectedDay
                      .hour(parseInt(hours))
                      .minute(parseInt(minutes));
                  }
                  selectedDate(finalDate.format("YYYY-MM-DD HH:mm"));
                  setShowModal(false);
                }
              }}
              activeOpacity={0.5}
            >
              <Text
                style={[
                  AppFontStyle.BOLD_ANYBODY_12,
                  {
                    paddingVertical: 12,
                    paddingHorizontal: 18,
                    color: AppColors.white,
                  },
                ]}
              >
                Save
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
};

export default DateTimePickerModal;

// Styles remain the same
const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: AppColors.blackTrans_000000a8,
  },
  datePickerContainer: {
    alignItems: "center",
  },
  datePicker: {
    width: 350,
    backgroundColor: AppColors.black_121212,
    borderRadius: 15,
    borderWidth: 1,
    borderColor: AppColors.buttonRed_DC2626,
    shadowRadius: 20,
    shadowColor: "#000",
    shadowOpacity: 0.1,
    shadowOffset: { width: 0, height: 0 },
    alignSelf: "center",
    marginVertical: 50,
  },
  datePickerView: {
    width: 350,
    padding: 15,
  },
  timePickerContainer: {
    marginTop: 15,
    paddingVertical: 10,
    backgroundColor: AppColors.black_121212,
  },
  timeList: {
    backgroundColor: AppColors.black_121212,
  },
  timeListContent: {
    paddingHorizontal: 10,
    alignItems: "center",
  },
  timeOption: {
    paddingVertical: 10,
    paddingHorizontal: 15,
    borderRadius: 10,
    marginHorizontal: 5,
    backgroundColor: AppColors.black_282828,
    minWidth: 60,
    alignItems: "center",
  },
});
